import "./iconify-offline";
import React, { lazy, Suspense } from 'react';
import { createRoot } from "react-dom/client";
import { BrowserRouter as Router, Routes, Route, Outlet } from 'react-router-dom';
import { ThemeProvider, SettingsProvider, defaultSettings } from 'minimal-ui';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { LicenseInfo } from '@mui/x-license-pro';
import { Toaster } from 'sonner';
import TopLoadingBar from './components/common/TopLoadingBar';
import ActiveTabSessionSync from './components/common/ActiveTabSessionSync';
import ProtectedRoute from './components/common/ProtectedRoute';
import UnloadLogoutNotice from './components/common/UnloadLogoutNotice';
import config from './components/common/Config';
import {
  ensureUnloadLastTabListener,
  primeTabSessionIfLoggedIn,
} from './utils/activeTabSession';
import { RoleProvider } from './contexts/RoleContext';
import { CompanySettingsProvider } from './contexts/CompanySettingsContext';
import { MegaMenu } from './components/mega-menu';
import Footer from './components/Footer';
import NewMarkDelivery from './components/NewMarkDelivery';
import ViewPickingList from './components/picking/ViewPickingList';
import SingleViewPickinglist from './components/picking/SingleViewPickinglist';
import { Box } from '@mui/material';
import "./assets/common-button.css";
import "./assets/main.css";
import "./assets/nprogress-custom.css";

function isChunkLoadError(error) {
  const name = error && error.name ? String(error.name) : "";
  const message = String(
    (error && error.message) || error || ""
  );
  return (
    name === "ChunkLoadError" ||
    message.includes("Loading chunk") ||
    message.includes("ChunkLoadError") ||
    message.includes("Failed to fetch dynamically imported module") ||
    message.includes("Importing a module script failed")
  );
}

function reloadOnceOnChunkError() {
  const key = "ebusso_chunk_reload";
  try {
    if (sessionStorage.getItem(key) === "1") {
      sessionStorage.removeItem(key);
      return false;
    }
    sessionStorage.setItem(key, "1");
  } catch (e) {
  }
  try {
    const url = new URL(window.location.href);
    url.searchParams.set("_cb", String(Date.now()));
    window.location.replace(url.toString());
  } catch (e) {
    window.location.reload();
  }
  return true;
}

class LazyRouteErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error) {
    if (isChunkLoadError(error)) {
      reloadOnceOnChunkError();
    }
  }
  render() {
    if (!this.state.hasError) return this.props.children;
    return (
      <div style={{ padding: 32, textAlign: "center" }}>
        <p>This page failed to load.</p>
        <button
          type="button"
          onClick={() => {
            try {
              sessionStorage.removeItem("ebusso_chunk_reload");
            } catch (e) {}
            window.location.reload();
          }}
        >
          Reload
        </button>
      </div>
    );
  }
}

window.addEventListener("unhandledrejection", (event) => {
  if (isChunkLoadError(event && event.reason)) {
    reloadOnceOnChunkError();
  }
});

window.addEventListener("error", (event) => {
  if (isChunkLoadError((event && event.error) || (event && event.message))) {
    reloadOnceOnChunkError();
  }
});

ensureUnloadLastTabListener(() => config.API_URL);

// Set MUI X license key (replace with your actual license key)
// For development/testing, you can use a trial key or purchase a license from https://mui.com/x/introduction/licensing/
LicenseInfo.setLicenseKey(
  process.env.REACT_APP_MUI_X_LICENSE_KEY ||
  '' // Add your license key here or set REACT_APP_MUI_X_LICENSE_KEY in .env file
);

const About = lazy(() => import('./components/About'));
const Login = lazy(() => import('./components/Login'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const ApiHealthDashboard = lazy(() => import('./services/apiHealth/Dashboard'));
const NewSales = lazy(() => import('./components/NewSales'));
const OrderHistory = lazy(() => import('./components/OrderHistory'));
const CreateManifest = lazy(() => import('./components/CreateManifest'));
const DataTable = lazy(() => import('./components/DataTable'));
const ManifestList = lazy(() => import('./components/ManifestList'));
const ManifestPrint = lazy(() => import('./components/dispatch/ManifestPrint'));
const OpenTrips = lazy(() => import('./components/OpenTrips'));
const OpenTripSummary = lazy(() => import('./components/OpenTripSummary'));
const CompleteTrips = lazy(() => import('./components/CompleteTrips'));
const TripsTabs = lazy(() => import('./components/TripsTabs'));
const CloseTripSummary = lazy(() => import('./components/CloseTripSummary'));
const ReturnList = lazy(() => import('./components/Return/ReturnList'));
const SalesView = lazy(() => import('./components/SalesView'));
const PickingCreate = lazy(() => import('./components/picking/PickingCreate'));
const PickingList = lazy(() => import('./components/picking/PickingList'));
const PickingTabs = lazy(() => import('./components/picking/PickingTabs'));
const SinglePickingList = lazy(() => import('./components/picking/SinglePickinglist'));
const EditPicking = lazy(() => import('./components/picking/EditPicking'));
const PickingHistory = lazy(() => import('./components/picking/PickingHistory'));
const ViewPicking = lazy(() => import('./components/picking/ViewPicking'));
const ShortQty = lazy(() => import('./components/picking/ShortQty'));
const ViewShortQty = lazy(() => import('./components/picking/ViewShortQty'));
const ConfirmShortQty = lazy(() => import('./components/picking/ConfirmShortQty'));
const Proforma = lazy(() => import('./components/picking/Proforma'));
const ProformaInvoice = lazy(() => import('./components/picking/ProformaInvoice'));
const CommonDatePicker = lazy(() => import('./components/datepicker/CommonDatePicker'));
const Edit_New_Invoice = lazy(() => import('./components/Edit_New_Invoice'));
const AddReturn = lazy(() => import('./components/Return/AddReturn'));
const AddReturnRhp = lazy(() => import('./components/Return/AddReturnRhp'));
const RoutePlaningInvoice = lazy(() => import('./components/RoutePlaningInvoice'));
const ClosedTripSummary = lazy(() => import('./components/ClosedTripSummary'));
const CloseTripSummeryReport = lazy(() => import('./components/ClosedTripSummary'));
const ModalComponent = lazy(() => import('./components/ModalComponent'));
const TableComponent = lazy(() => import('./components/TableComponent'));
const Products = lazy(() => import('./components/Master/Products/Products'));
const Add_Products = lazy(() => import('./components/Master/Products/Add_Products'));
const Add_Products_Dynamic = lazy(() => import('./components/Master/Products/Add_Products_Dynamic'));
const Previous_Product = lazy(() => import('./components/Master/Products/PreviousProduct'));
const Supplier = lazy(() => import('./components/Master/Supplier/Supplier'));
const Add_Supplier = lazy(() => import('./components/Master/Supplier/Add_Supplier'));
const Edit_Supplier = lazy(() => import('./components/Master/Supplier/Edit_Supplier'));
const Customers = lazy(() => import('./components/Master/Customers/Customers'));
const Edit_Customer = lazy(() => import('./components/Master/Customers/Edit_Customer'));
const Edit_Customer_Dynamic = lazy(() => import('./components/Master/Customers/Edit_Customer_Dynamic'));
const AddCustomersFormDynamicPage = lazy(() => import('./components/Master/Customers/AddCustomersFormDynamicPage'));
const Contra_Price = lazy(() => import('./components/Master/Customers/ContraPrice'));
const ContraList = lazy(() => import('./components/Master/Customers/ContraList'));
const Contra_Price_Edit = lazy(() => import('./components/Master/Customers/Contra_Price_Edit'));
const Users = lazy(() => import('./components/Master/Users/Users'));
const User_Profile = lazy(() => import('./components/Master/Users/User_Profile'));
const Brands = lazy(() => import('./components/Master/Brands/Brands'));
const Units = lazy(() => import('./components/Master/Units/Units'));
const Product_Category = lazy(() => import('./components/Master/Product_Category'));
const Products_Price_Variation = lazy(() => import('./components/Master/PriceVariation/Products_Price_Variation'));
const Customer_Credit_Facility = lazy(() => import('./components/Master/CustomerCredit/Customer_Credit_Facility'));
const CustomerNotOrderReport = lazy(() => import('./components/reports/CustomerNotOrderReport'));
const MisReport = lazy(() => import('./components/reports/MisReport'));
const MisProductReport = lazy(() => import('./components/reports/MisProductReport'));
const InventorySalesReport = lazy(() => import('./components/reports/InventorySalesReport'));
const PurchaseReport = lazy(() => import('./components/reports/PurchaseReport'));
const CrossOffReport = lazy(() => import('./components/reports/CrossOffReport'));
const SalesRepReport = lazy(() => import('./components/reports/SalesRepReport'));
const DsrReport = lazy(() => import('./components/reports/DsrReport'));
const ProfitLossReport = lazy(() => import('./components/reports/ProfitLossReport'));
const StockReport = lazy(() => import('./components/reports/StockReport'));
const BatchWiseStockReport = lazy(() => import('./components/reports/BatchWiseStockReport'));
const BatchWiseStockDetailsReport = lazy(() => import('./components/reports/BatchWiseStockDetailsReport'));
const BritDualStockReport = lazy(() => import('./components/reports/BritDualStockReport'));
const BritStockTransferReport = lazy(() => import('./components/reports/BritStockTransferReport'));
const NewRSales = lazy(() => import('./components/nsales/NewRSales'));
const HoldList = lazy(() => import('./components/nsales/HoldList'));
const SalesOrderHistory = lazy(() => import('./components/nsales/SalesOrderHistory'));
const EditSalesInvoice = lazy(() => import('./components/nsales/EditSalesInvoice'));
const EditSalesInvoice_Sdm = lazy(() => import('./components/nsales/EditSalesInvoice_Sdm'));
const Edit_delivered_invoice = lazy(() => import('./components/trip/Edit_delivered_invoice'));
const Edit_delivered_invoice_sdm = lazy(() => import('./components/trip/Edit_delivered_invoice_sdm'));
const RejectSales = lazy(() => import('./components/nsales/RejectSales'));
const SummaryPrint = lazy(() => import('./components/trip/SummaryPrint'));
const MultipleSummaryPrint = lazy(() => import('./components/trip/MultipleSummaryPrint'));
const OrderSummaryPrint = lazy(() => import('./components/nsales/OrderSummaryPrint'));
const MonthSales = lazy(() => import('./components/dashboard/MonthSalesList'));
const CreateNewManifest = lazy(() => import('./components/dispatch/CreateNewManifest'));
const CreateManifestPrint = lazy(() => import('./components/dispatch/CreateManifestPrint'));
const StockTakeHistory = lazy(() => import('./components/stockTake').then(module => ({ default: module.StockTakeHistory })));
const StockTakeHistoryList = lazy(() => import('./components/stockTake').then(module => ({ default: module.StockTakeHistoryList })));
const StockTakeList = lazy(() => import('./components/stockTake').then(module => ({ default: module.StockTakeList })));
const StartStockTake = lazy(() => import('./components/stockTake').then(module => ({ default: module.StartStockTake })));
const PurchasesOrder = lazy(() => import('./components/purchases').then(module => ({ default: module.PurchasesOrder })));
const PurchasesHistory = lazy(() => import('./components/purchases').then(module => ({ default: module.PurchasesHistory })));
const PurchasesAddFrom = lazy(() => import('./components/purchases').then(module => ({ default: module.PurchasesAddFrom })));
const EditPurchasesFrom = lazy(() => import('./components/purchases').then(module => ({ default: module.EditPurchasesFrom })));
const SuggestedPO = lazy(() => import('./components/purchases').then(module => ({ default: module.SuggestedPO })));
const PurchaseViewPrint = lazy(() => import('./components/purchases').then(module => ({ default: module.PurchaseViewPrint })));
const PurchasePOViewPrint = lazy(() => import('./components/purchases').then(module => ({ default: module.PurchasePOViewPrint })));
const AddPurchasesGRN = lazy(() => import('./components/purchases').then(module => ({ default: module.AddPurchasesGRN })));
const PurchaseHistoryViewPrint = lazy(() => import('./components/purchases').then(module => ({ default: module.PurchaseHistoryViewPrint })));
const PushSage = lazy(() => import('./components/account').then(module => ({ default: module.PushSage })));
const AllPushInvoice = lazy(() => import('./components/account').then(module => ({ default: module.AllPushInvoice })));
const PaymentProcess = lazy(() => import('./components/account').then(module => ({ default: module.PaymentProcess })));
const CustomerAccounts = lazy(() => import('./components/account').then(module => ({ default: module.CustomerAccounts })));
const CustomerTransactionEnquiry = lazy(() => import('./components/account').then(module => ({ default: module.CustomerTransactionEnquiry })));
const SupplierLedger = lazy(() => import('./components/account').then(module => ({ default: module.SupplierLedger })));
const PaymentLadger = lazy(() => import('./components/account').then(module => ({ default: module.PaymentLadger })));
const MyLadger = lazy(() => import('./components/account').then(module => ({ default: module.MyLadger })));
const ProductLadger = lazy(() => import('./components/account').then(module => ({ default: module.ProductLadger })));
const ProductLadgerView = lazy(() => import('./components/account').then(module => ({ default: module.ProductLadgerView })));
const AmountDueCustomer = lazy(() => import('./components/account/AmountDueCustomerList').then(module => ({ default: module.default })));
const Add_Promos = lazy(() => import('./components/Promotions_old/Add_Promos'));
const Edit_Promos = lazy(() => import('./components/Promotions_old/Edit_Promos'));
const Promos = lazy(() => import('./components/Minimal/src/sections/promotions').then(module => ({ default: module.PromotionMasterList })));
const AddPromotion = lazy(() => import('./components/Minimal/src/sections/promotions').then(module => ({ default: module.AddPromotion })));
const EditPromotion = lazy(() => import('./components/Minimal/src/sections/promotions/edit-promotion'));
const Create_similar_promo = lazy(() => import('./components/Promotions_old/Create_similar_promo'));
const Slider_settings = lazy(() => import('./components/slider_settings/Slider_settings'));
const PromoFlipbook = lazy(() => import('./features/promo-flipbook/PromoFlipbook'));
const PromoFlipbookPage = () => {
  const token =
    (typeof localStorage !== 'undefined' &&
      (localStorage.getItem('token') || localStorage.getItem('accessToken'))) ||
    '';
  return <PromoFlipbook token={token} />;
};
const Catalogue = lazy(() => import('./components/Catalogue/Catalogue'));
const Epos = lazy(() => import('./components/sales/Epos'));
const EposSales = lazy(() => import('./components/sales/EposSales'));
const NewEpos = lazy(() => import('./components/sales/EposRhp/view/NewEposEntry'));
const EditNewEpos = lazy(() => import('./components/sales/EditNewEpos'));
const EditEposSales = lazy(() => import('./components/sales/EditEposSales'));
const ViewEposSales = lazy(() => import('./components/sales/ViewEposSales'));
const Inventory_Report = lazy(() => import('./components/sales/Inventory_Report'));
const OffersPage = lazy(() => import('./components/sales/Promotion/view/offers-page'));
const StockAdjustment = lazy(() => import('./components/purchases/StockAdjustment'));
const Application_Setting = lazy(() => import('./components/Settings/Application_Setting'));
const ReturnListPrint = lazy(() => import('./components/Return/ReturnListPrint'));
const ReturnListPrintRhp = lazy(() => import('./components/Return/ReturnListPrintRhp'));
const NewSalesDashbard = lazy(() => import('./components/dashboard/NewSales'));
const SalesOrderDashbard = lazy(() => import('./components/dashboard/SaleOrder'));
const DeliveredInvoice = lazy(() => import('./components/dashboard/DeliveredInvoice'));
const InprogressInvoice = lazy(() => import('./components/dashboard/InprogressInvoice'));
const OrderAccepted = lazy(() => import('./components/dashboard/OrderAccepted'));
const PicklistGenerated = lazy(() => import('./components/dashboard/PicklistGenerated'));
const PickingConfirm = lazy(() => import('./components/dashboard/PickingConfirm'));
const TopSales = lazy(() => import('./components/dashboard/TopSales'));
const MaxValueOrder = lazy(() => import('./components/dashboard/MaxValueOrder'));
const ManifestGenerated = lazy(() => import('./components/dashboard/ManifestGenerated'));
const DeliveryConfirmed = lazy(() => import('./components/dashboard/DeliveryConfirmed'));
const OrderLocationReport = lazy(() => import('./components/reports/OrderLocationReport'));
const SalesProductReport = lazy(() => import('./components/reports/SalesProductReport'));
const NewSalesInvoice = lazy(() => import('./components/nsales/NewSalesInvoice'));
const PurchaseOrderInvoice = lazy(() => import('./components/purchases/PurchaseOrderInvoice'));
const EditProforma = lazy(() => import('./components/proforma/EditProforma'));
const EditProforma_sdm = lazy(() => import('./components/proforma/EditProforma_Sdm'));
const Registration = lazy(() => import('./components/Settings/Registration'));
const Common_Invoice = lazy(() => import('./components/nsales/Common_Invoice'));
const Temp = lazy(() => import('./components/Temp'));
const NewFile = lazy(() => import('./components/nsales/NewFile'));
const PaymentAllocation = lazy(() => import('./components/account/InvoicePaymentAllocation'));
const Cashier_Report = lazy(() => import('./components/reports/Cashier_Report'));
const ExpiryReport = lazy(() => import('./components/reports/ExpiryReport'));
const Order_Receipt_sticker = lazy(() => import('./components/sales/Order_Receipt_sticker'));
const Price_sticker = lazy(() => import('./components/Master/Products/Price_sticker'));
const Promotion_Sale_slip = lazy(() => import('./components/Promotion_Sale_slip'));
const IncompleteOrderList = lazy(() => import('./components/sales/IncompleteOrderList'));
const Quick_Order = lazy(() => import('./components/sales/Quick_Order'));
const QuickOrder = lazy(() => import('./components/sales/QuickOrder/view/quick-order'));
const InvoiceView = lazy(() => import('./components/sales/Invoice/InvoiceView'));
const TaxInvoiceView = lazy(() => import('./components/sales/Invoice/TaxInvoiceView'))
const Invoice_Format = lazy(() => import('./components/Invoice_Format'));
const Customer_Onboarding = lazy(() => import('./components/Customer_Onboarding'));
const BarcodeScannerComponent = lazy(() => import('./components/sales/BarcodeScannerComponent'));
const EmailTemplate = lazy(() => import('./components/Email_template/EmailTemplateEditor'));
const ShopifyCategories = lazy(() => import('./components/picking/ShopifyCategories'));
const ShopifyProductList = lazy(() => import('./components/picking/ShopifyProduct'));
const AmazonProductList = lazy(() => import('./components/amazon/AmazonProduct'));
const MapRoute = lazy(() => import('./components/dispatch/MapRoute'));
const PaymentCollection = lazy(() => import('./components/reports/PaymentCollection'));
const ChangePermissions = lazy(() => import('./components/Settings/ChangePermissions'));
const ProductFormFieldSettings = lazy(() => import('./components/Settings/ProductFormFieldSettings'));
const CustomerFormFieldSettings = lazy(() => import('./components/Settings/CustomerFormFieldSettings'));
const CompanySettings = lazy(() => import('./components/Settings/CompanySettings'));
const TransactionsReport = lazy(() => import('./components/reports/TransactionsReport'));
const DaySummaryReport = lazy(() => import('./components/reports/DaySummaryReport'));
const PurchaseOrderReport = lazy(() => import('./components/reports/PurchaseOrderReport'));
const SalesOrderReport = lazy(() => import('./components/reports/SalesOrderReport'));
const PushInvoiceReport = lazy(() => import('./components/reports/PushInvoiceReport'));
const PushCreditNoteReport = lazy(() => import('./components/reports/PushCreditNoteReport'));
const CustomerMonthlySalesReport = lazy(() => import('./components/reports/CustomerMonthlySalesReport'));
const NewProductsTransactionReport = lazy(() => import('./components/reports/NewProductsTransactionReport'));
const CatalogueNew = lazy(() => import('./components/Catalogue/CatalogueNew'));
const NotFound = lazy(() => import('./components/error/NotFound'));
const SalesInvoice = lazy(() => import('./components/sales/Invoice/print/view/sales-invoice'));
const ViewPromotion = lazy(() => import('./components/Minimal/src/sections/promotions/view-promotion'));
const Notification = lazy(() => import('./components/Notifications/Notification'));
const CreateNotification = lazy(() => import('./components/Notifications/CreateNotification'));
const EditNotification = lazy(() => import('./components/Notifications/EditNotification'));
const LocationTrackReport = lazy(() => import('./components/reports/LocationTrackReport'));
const AnalysisCanvas = lazy(() => import('./ANALYSIS.canvas.tsx'));
const queryClient = new QueryClient();

const MainLayout = () => {
  return (
    <ProtectedRoute>
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <ActiveTabSessionSync />
      <Box
        component="header"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1100,
          backgroundColor: '#fff',
        }}
      >
        <MegaMenu />
      </Box>
      <Box
        component="main"
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Outlet />
        </Box>
      </Box>
      <Box
        component="footer"
        sx={{
          position: 'sticky',
          bottom: 0,
          zIndex: 1100,
          backgroundColor: '#fff',
        }}
      >
        <Footer />
      </Box>
    </Box>
    </ProtectedRoute>
  );
};

const PublicLayout = () => {
  return <Outlet />;
};

function App() {
  return (
    <RoleProvider>
      <CompanySettingsProvider>
      <Router basename="/">
        <LazyRouteErrorBoundary>
        <Suspense fallback={<TopLoadingBar />}>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Login />} />
              <Route path="/BarcodeScannerComponent" element={<BarcodeScannerComponent />} />
              <Route path="/DataTable" element={<DataTable />} />
              <Route path="/login" element={<Login />} />
              <Route path="/api-health-monitor" element={<ApiHealthDashboard />} />
            </Route>
            <Route element={<MainLayout />}>
              <Route path="/About" element={<About />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/dashboard/:prefix/:userId" element={<Dashboard />} />
              <Route path="/new_sales" element={<NewSales />} />
              <Route path="/order_history" element={<OrderHistory />} />
              <Route path="/create_manifest" element={<CreateManifest />} />
              <Route path="/route_planing_invoice" element={<RoutePlaningInvoice />} />
              <Route path="/manifest_list" element={<ManifestList />} />
              <Route path="/manifest_list/ManifestPrint/:manifestId" element={<ManifestPrint />} />
              <Route path="/manifest_list/mark_delivery/:manifestId" element={<NewMarkDelivery />} />
              <Route path="/demoModal/:manifestId" element={<ModalComponent />} />
              <Route path="/open_trips" element={<TripsTabs />} />
              <Route path="/open_trips/trip_summary/:tripId" element={<OpenTripSummary />} />
              <Route path="/complete_trips" element={<CompleteTrips />} />
              <Route path="/complete_trips/closed_trip_summary/:tripId" element={<CloseTripSummary />} />
              <Route path="/closed_trip_summary/:tripId" element={<CloseTripSummeryReport />} />
              <Route path="/return_list" element={<ReturnList />} />
              <Route path="/sales_view" element={<SalesView />} />
              <Route path="/picking/picking_create" element={<PickingCreate />} />
              <Route path="/picking/picking_list" element={<PickingTabs />} />
              <Route path="/picking/single_picking_list" element={<SinglePickingList />} />
              <Route path="/picking/edit_picking/:picklistNo" element={<EditPicking />} />
              <Route path="/picking/picking_history" element={<PickingHistory />} />
              <Route path="/picking/short_qty" element={<ShortQty />} />
              <Route path="/picking/view_short_qty/:picklistNo" element={<ViewShortQty />} />
              <Route path="/picking/confirm_short_qty/:picklistNo/:productId/:orderType" element={<ConfirmShortQty />} />
              <Route path="/picking/proforma" element={<Proforma />} />
              <Route path="/picking/view_picking/:picklistNo" element={<ViewPicking />} />
              <Route path="/sales/view_picking_list/:picklistNo" element={<ViewPickingList />} />
              <Route path="/sales/single_view_picking_list/:picklistNo" element={<SingleViewPickinglist />} />
              <Route path="/sales/invoice/:id" element={<InvoiceView />} />
              <Route path="/sales/tax/invoice/:id" element={<TaxInvoiceView />} />

              <Route path="/DateRangePicker" element={<CommonDatePicker />} />
              <Route path="/proforma_invoice" element={<ProformaInvoice />} />
              <Route path="/edit_new_invoice" element={<Edit_New_Invoice />} />
              <Route path="/return/add_return" element={<AddReturn />} />
              <Route path="/return/add_return_rhp" element={<AddReturnRhp />} />
              <Route path="/products" element={<Products />} />
              <Route path="/products/add_product" element={<Add_Products />} />
              <Route path="/products/add_product_dynamic" element={<Add_Products_Dynamic />} />
              <Route path="/products/previous_pricing/:productId" element={<Previous_Product />} />
              <Route path="/supplier" element={<Supplier />} />
              <Route path="/supplier/add_supplier" element={<Add_Supplier />} />
              <Route path="/supplier/edit_supplier/:supplierId" element={<Edit_Supplier />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/customers/add_customer_dynamic" element={<AddCustomersFormDynamicPage />} />
              <Route path="/ContraList" element={<ContraList />} />
              <Route path="/customer/edit_customer/:customerId" element={<Edit_Customer />} />
              <Route path="/customer/edit_customer_dynamic/:customerId" element={<Edit_Customer_Dynamic />} />
              <Route path="/customer/contra_price/:customerId" element={<Contra_Price />} />
              <Route path="/customers/contra_price_edit/:customerId" element={<Contra_Price_Edit />} />
              <Route path="/users" element={<Users />} />
              <Route path="/user/edit_profile/:userId" element={<User_Profile />} />
              <Route path="/Brands" element={<Brands />} />
              <Route path="/Units" element={<Units />} />
              <Route path="/product_category" element={<Product_Category />} />
              <Route path="/products_price_variation" element={<Products_Price_Variation />} />
              <Route path="/Customer_Credit_Facility" element={<Customer_Credit_Facility />} />
              <Route path="/testTable" element={<TableComponent />} />
              <Route path="reports/customer_not_order_report" element={<CustomerNotOrderReport />} />
              <Route path="reports/mis_report" element={<MisReport />} />
              <Route path="reports/mis_product_report" element={<MisProductReport />} />
              <Route path="reports/inventory_sales_report" element={<InventorySalesReport />} />

              <Route path="reports/purchase_report" element={<PurchaseReport />} />

              <Route path="reports/missing_order" element={<CrossOffReport />} />
              <Route path="reports/sales_report" element={<SalesRepReport />} />
              <Route path="reports/dsr_report" element={<DsrReport />} />
              <Route path="reports/profit_loss_report" element={<ProfitLossReport />} />
              <Route path="reports/stock_report" element={<StockReport />} />
              <Route path="reports/batchWise_stock_report" element={<BatchWiseStockReport />} />
              <Route path="reports/batchWise_stock_details_report/:id" element={<BatchWiseStockDetailsReport />} />
              <Route path="reports/brit_dual_stock" element={<BritDualStockReport />} />
              <Route path="reports/brit_stock_transfer_report" element={<BritStockTransferReport />} />
              <Route path="sales/newsales" element={<NewRSales />} />
              <Route path="sales/holdList" element={<HoldList />} />
              <Route path="sales/shistory" element={<SalesOrderHistory />} />
              <Route path="sales/reject_sales" element={<RejectSales />} />
              <Route path="/sales/edit_invoice/:orderId" element={<EditSalesInvoice />} />
              <Route path="/sales/edit/:orderId" element={<EditNewEpos />} />
              <Route path="/sales/edit_invoice_sdm/:orderId" element={<EditSalesInvoice_Sdm />} />
              <Route path="/trip/Edit_delivered_invoice/:tripId/:orderId" element={<Edit_delivered_invoice />} />
              <Route path="/trip/Edit_delivered_invoice_sdm/:tripId/:orderId" element={<Edit_delivered_invoice_sdm />} />
              <Route path="/trip_summary/print/MultipleSummaryPrint" element={<MultipleSummaryPrint />} />
              <Route path="/trip_summary/print/:orderId" element={<SummaryPrint />} />
              <Route path="/reports/mis_report/print/:orderId" element={<SummaryPrint />} />
              <Route path="/picking/picking_list/view/:orderId" element={<SummaryPrint />} />
              <Route path="order_summary/print/:orderId" element={<OrderSummaryPrint />} />
              <Route path="/monthsales" element={<MonthSales />} />
              <Route path="delivery/route_planing" element={<CreateNewManifest />} />
              <Route path="/delivery/route_planing/view/:orderId" element={<CreateManifestPrint />} />
              <Route path="stock_take/history" element={<StockTakeHistory />} />
              <Route path="/products/show_history_of_stock_take_list/:stockId" element={<StockTakeHistoryList />} />
              <Route path="stock_take/stock_take_list" element={<StockTakeList />} />
              <Route path="stock_take/start_stock_take" element={<StartStockTake />} />
              <Route path="purchases/purchases_order" element={<PurchasesOrder />} />
              <Route path="purchases/purchases_grn" element={<PurchasesHistory />} />
              <Route path="purchases/add" element={<PurchasesAddFrom />} />
              <Route path="purchases/stockAdjustment" element={<StockAdjustment />} />
              <Route path="/purchases/edit_po/:orderId" element={<EditPurchasesFrom />} />
              <Route path="/purchases/suggested" element={<SuggestedPO />} />
              <Route path="/purchases/view/:orderId" element={<PurchaseViewPrint />} />
              <Route path="/purchases/view_po/:orderId" element={<PurchasePOViewPrint />} />
              <Route path="/purchases/view_grn/:orderId" element={<PurchaseHistoryViewPrint />} />
              <Route path="/purchases/add_grn/:orderId" element={<AddPurchasesGRN />} />
              <Route path="account/push_sage" element={<PushSage />} />
              <Route path="account/push_invoice" element={<AllPushInvoice />} />
              <Route path="account/payments" element={<PaymentProcess />} />
              <Route path="account/customer_accounts" element={<CustomerAccounts />} />
              <Route path="account/customer_transaction_enquiry" element={<CustomerTransactionEnquiry />} />
              <Route path="account/amount_due_customer" element={<AmountDueCustomer />} />
              <Route path="account/supplier_ledger" element={<SupplierLedger />} />
              <Route path="account/payment_ladger" element={<PaymentLadger />} />
              <Route path="account/my_ledger" element={<MyLadger />} />
              <Route path="Promotions/Promos" element={<Promos />} />
              <Route path="Promotions/Create_similar_promo/:id" element={<Create_similar_promo />} />
              <Route path="/Promotions/Add_Promos" element={<Add_Promos />} />
              <Route path="/Promotions/Add_Promotion" element={<AddPromotion />} />
              <Route path="/Promotions/Edit_Promotion/:id" element={<EditPromotion />} />
              <Route path="/Promotions/View_Promotion/:id" element={<ViewPromotion />} />
              <Route path="/Promotions/Edit_Promos" element={<Edit_Promos />} />
              <Route path="slider_settings" element={<Slider_settings />} />
              <Route path="/promo-flipbook" element={<PromoFlipbookPage />} />
              <Route path="/Promotions/Edit_Promos/:id" element={<Edit_Promos />} />
              <Route path="Catalogue" element={<Catalogue />} />
              <Route path="sales/Epos" element={<Epos />} />
              <Route path="sales/epos/new" element={<NewEpos />} />
              <Route path="sales/epos/edit/:orderId" element={<EditNewEpos />} />
              <Route path="sales/epos_sales" element={<EposSales />} />
              <Route path="sales/epos_sales/edit/:orderId" element={<EditEposSales />} />
              <Route path="sales/epos_sales/view/:orderId" element={<ViewEposSales />} />
              <Route path="sales/offers/promotions" element={<OffersPage />} />
              <Route path="dashboard/NewSales" element={<NewSalesDashbard />} />
              <Route path="dashboard/DeliveredInvoice" element={<DeliveredInvoice />} />
              <Route path="dashboard/InprogressInvoice" element={<InprogressInvoice />} />
              <Route path="dashboard/SalesOrder" element={<SalesOrderDashbard />} />
              <Route path="dashboard/OrderAccepted" element={<OrderAccepted />} />
              <Route path="dashboard/picklistGenerated" element={<PicklistGenerated />} />
              <Route path="dashboard/PickingConfirm" element={<PickingConfirm />} />
              <Route path="dashboard/TopSales" element={<TopSales />} />
              <Route path="dashboard/MaxValueOrder" element={<MaxValueOrder />} />
              <Route path="dashboard/ManifestGenerated" element={<ManifestGenerated />} />
              <Route path="dashboard/DeliveryConfirmed" element={<DeliveryConfirmed />} />
              <Route path="account/product_ledger" element={<ProductLadger />} />
              <Route path="payments/view_product_ledger/:productId/:startDate/:endDate/:orderType" element={<ProductLadgerView />} />
              {/* <Route path="sales/Finish_Pay" element={<Finish_Pay />} />
        <Route path="sales/Incomplete_Order" element={<Incomplete_Order />} /> */}
              <Route path="/returns/view/:orderId" element={<ReturnListPrint />} />
              <Route path="/returns/view_rhp/:orderId" element={<ReturnListPrintRhp />} />
              <Route path="/reports/lat_long_report" element={<OrderLocationReport />} />
              <Route path="/reports/sales_product_report" element={<SalesProductReport />} />
              <Route path="new_sales/invoice/:orderId" element={<NewSalesInvoice />} />
              <Route path="purchaseOrder/invoice/:orderId" element={<PurchaseOrderInvoice />} />
              <Route path="edit/proforma/:orderId" element={<EditProforma />} />
              <Route path="edit/proforma_sdm/:orderId" element={<EditProforma_sdm />} />
              <Route path="Settings/Registration" element={<Registration />} />
              <Route path="sales/Common_Invoice" element={<Common_Invoice />} />
              <Route path="Temp" element={<Temp />} />
              <Route path="storepdf/:orderId" element={<NewFile />} />
              <Route path="payments/payment_allocation/:customer_id" element={<PaymentAllocation />} />
              <Route path="reports/Cashier_Report" element={<Cashier_Report />} />
              <Route path="reports/Expiry_report" element={<ExpiryReport />} />
              <Route path="sales/Order_Receipt_sticker" element={<Order_Receipt_sticker />} />
              <Route path="product/price_sticker/:product_id" element={<Price_sticker />} />
              <Route path="Promotion_Sale_slip" element={<Promotion_Sale_slip />} />
              <Route path="sales/InventoryReport" element={<Inventory_Report />} />

              <Route path="sales/IncompleteOrderList" element={<IncompleteOrderList />} />
              <Route path="sales/Quick_Order" element={<Quick_Order />} />
              <Route path="sales/quick-order" element={<QuickOrder />} />
              <Route path="sales/quick-order-index-search" element={<QuickOrder />} />

              <Route path="/Invoice_Format" element={<Invoice_Format />} />
              <Route path="/EmailTemplate" element={<EmailTemplate />} />
              <Route path="/Customer_Onboarding" element={<Customer_Onboarding />} />

              <Route path="/shopifyCategoryList" element={<ShopifyCategories />} />
              <Route path="/shopifyProductList" element={<ShopifyProductList />} />
              <Route path="/amazonProductList" element={<AmazonProductList />} />
              <Route path="/manifest/:manifestId/map" element={<MapRoute />} />
              <Route path="/reports/payment_collection" element={<PaymentCollection />} />
              <Route path="/settings/change_permissions" element={<ChangePermissions />} />
              <Route path="/settings/change_permissions/:roleId" element={<ChangePermissions />} />
              <Route path="/settings/product_form_fields" element={<ProductFormFieldSettings />} />
              <Route path="/settings/customer_form_fields" element={<CustomerFormFieldSettings />} />
              <Route path="/dashboard/company-settings" element={<CompanySettings />} />
              <Route path="reports/TransactionsReport" element={<TransactionsReport />} />
              <Route path="reports/DaySummaryReport" element={<DaySummaryReport />} />
              <Route path="reports/PurchaseOrderReport" element={<PurchaseOrderReport />} />
              <Route path="reports/SalesOrderReport" element={<SalesOrderReport />} />
              <Route path="reports/PushInvoiceReport" element={<PushInvoiceReport />} />
              <Route path="reports/PushCreditNoteReport" element={<PushCreditNoteReport />} />
              <Route path="reports/NewProductsTransactionReport" element={<NewProductsTransactionReport />} />
              <Route path="*" element={<NotFound />} />
              <Route path="reports/CustomerMonthlySalesReport" element={<CustomerMonthlySalesReport />} />
              <Route path="sales/invoice/print/:id" element={<SalesInvoice />} />
              <Route path="/Notifications/Notification" element={<Notification />} />
              <Route path="/Notifications/CreateNotification" element={<CreateNotification />} />
              <Route path="/Notifications/EditNotification" element={<EditNotification />} />
              <Route path="reports/LocationTrackReport" element={<LocationTrackReport />} />
              <Route path="/analysis/price-mapping" element={<AnalysisCanvas />} />
            </Route>
          </Routes>
        </Suspense>
        </LazyRouteErrorBoundary>
      </Router>
      </CompanySettingsProvider>
    </RoleProvider>
  );
}
/*
const container = document.getElementById("root");
const root = createRoot(container);

root.render(
 <React.StrictMode>
    <App />
 </React.StrictMode>
);*/
// (‿|‿)

const container = document.getElementById('root');

primeTabSessionIfLoggedIn();

const root = createRoot(container);

// Render the app using the root
root.render(
  /* <React.StrictMode>*/
  <QueryClientProvider client={queryClient}>
    <SettingsProvider defaultSettings={defaultSettings}>
      <ThemeProvider>
        <App />
        <UnloadLogoutNotice />
        <Toaster
          position="top-right"
          richColors
          toastOptions={{
            style: {
              background: '#fff',
            },
          }}
        />
      </ThemeProvider>
    </SettingsProvider>
  </QueryClientProvider>
  /* </React.StrictMode>*/
);

//serviceWorkerRegistration.unregister();
